import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import Headline from 'components/Headline/Headline';
import Hero from 'components/Hero/Hero';
import MediaSlider from 'components/MediaSlider/MediaSlider';
import QuoteSection from 'components/QuoteSection/QuoteSection';
import RichText from 'components/RichText/RichText';
import Text from 'components/Text/Text';
import { ReactComponent as SVGLinkArrow } from 'svgs/link.svg';
import { setTypography } from 'utils/styles/mixins';
import { mediaDesktop, mediaTablet } from 'utils/styles/responsive';

export const Wrapper = styled(motion.div)`
  width: 100vw;
  display: grid;

  padding: 120px 0;
  overflow-x: hidden;

  ${mediaTablet(`
    padding: 153px 0 360px;
  `)}

  grid-template-columns: [out-left] 16px [main-section] auto [out-right] 16px;

  ${mediaDesktop(`
    grid-template-columns: [out-left] 124px [main-section] auto [out-right] 124px;
  `)}
`;

export const CaseStudySectionGrid = styled(motion.section)<{
  fullWidth?: boolean;
}>`
  * {
    letter-spacing: -0.02em !important;
  }

  display: grid;
  grid-template-rows: 1fr;
  grid-gap: 32px;

  overflow: visible;

  margin: 16px 0;

  ${({ fullWidth }) =>
    fullWidth
      ? css`
          grid-column: out-left/-1;
        `
      : css`
          grid-column: main-section/out-right;
        `}

  grid-template-columns: repeat(
          ${({ theme }) => theme.layout.columns.max},
          [content] 1fr
  );

  ${({ theme }) =>
    mediaTablet(`
    grid-template-columns: repeat(
      ${theme.layout.columns.max},
      [content] 1fr
    );
  `)}
`;

export const CaseStudyInfoSectionGrid = styled(CaseStudySectionGrid)`
  * {
    letter-spacing: -0.02em !important;
  }

  margin: 40px 0;

  ${mediaTablet(`
    margin: 120px 0;
  `)}
`;

export const HeroWrapper = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: main-section / out-right;
`;

export const HeroMedia = styled(Hero)`
  margin: 56px 0 56px -50%;
  width: 200%;

  > div {
    position: relative;
    width: 100%;
    height: auto;
  }

  ${mediaTablet(`
    width: 100%;
    margin: 40px 0 80px;
  `)}

  min-height: 0;
`;

export const IntroWrapper = styled.div`
  margin: 0;

  grid-column: 1 / -1;

  ${mediaTablet(`
    grid-column: content 7 / content 12;
  `)}
`;

export const IntroSectionListWrapper = styled.div`
  margin-top: 32px;

  ${mediaTablet`
    margin-top: 40px;
  `}
`;

export const IntroSectionText = styled(Text)`
  margin-bottom: 8px;
  ${mediaTablet`
    margin-bottom: 16px;
  `}
`;

export const IntroSectionList = styled.ul`
  margin-top: 9px;

  list-style: none;
`;

export const IntroSectionListItem = styled.li`
  margin: 8px 8px 8px 0;

  display: inline-flex;
`;

export const LinkArrow = styled(SVGLinkArrow)`
  width: 12px;

  margin-right: 12px;
`;

export const CaseStudyMediaSlider = styled(MediaSlider)`
  grid-column: 1/-1;

  margin: 112px 0 88px;

  ${mediaTablet`
    margin: 260px 0 160px;
  `}
`;

export const CaseStudyQuoteSection = styled(QuoteSection)`
  grid-column: 1/-1;
`;

export const CaseStudyLeftSideIntro = styled.div`
  grid-column: 1 / -1;

  button {
    padding: 10px 20px;
  }

  ${mediaTablet(`
    grid-column: content 1 / content 7;
  `)}
`;

export const RichTextComp = styled(RichText)`
  h1,
  h1 span {
    ${({ theme: { locale } }) => setTypography('heading1', locale)};
  }
  h2,
  h2 span,
  p {
    ${({ theme: { locale } }) => setTypography('heading2', locale)};
  }
  h3,
  h3 span {
    ${({ theme: { locale } }) => setTypography('heading3', locale)};
  }
  h4,
  h4 span {
    ${({ theme: { locale } }) => setTypography('heading4', locale)};
  }
  h5,
  h5 span {
    ${({ theme: { locale } }) => setTypography('heading5', locale)};
  }
  h6,
  h6 span {
    ${({ theme: { locale } }) => setTypography('body', locale)};
  }
`;

export const HeadlineComp = styled(Headline)`
  div > * {
    line-height: 0.95;
  }
`;
